import type { HTMLProps, ReactElement } from 'react';
import { useId } from 'react';
import cx from 'clsx';
import { IconCrossRoundedFilled } from '@repo/monochrome-icons';
import styles from './choice-chip.module.css';

export type ChoiceChipSize = 'medium' | 'small';
export type ChoiceChipVariant = 'primary' | 'secondary';

interface ChoiceChipProps<T>
  extends Omit<HTMLProps<HTMLButtonElement>, 'onChange' | 'size' | 'type' | 'value'> {
  value: T;
  size?: ChoiceChipSize;
  variant?: ChoiceChipVariant;
  isChecked?: boolean;
  isClearable?: boolean;
  isCollapsed?: boolean;
  isDisabled?: boolean;
  onChange?: (option: T) => Promise<void> | void;
}

export function ChoiceChip<T>({
  value,
  size = 'medium',
  variant = 'primary',
  isChecked,
  isClearable,
  isDisabled,
  isCollapsed,
  onChange,
  className,
  children,
  ...props
}: ChoiceChipProps<T>): ReactElement {
  const optionId = useId();

  const stringValue = String(value);
  const showClearButton = isClearable && isChecked && !props.disabled;

  function handleSelect(): void {
    if (isDisabled) return;

    void onChange?.(value);
  }

  return (
    <button
      aria-hidden={isCollapsed ? 'true' : 'false'}
      aria-labelledby={optionId}
      aria-selected={isChecked ? 'true' : 'false'}
      className={cx(styles.chip, styles[size], styles[variant], className)}
      data-chip-option=""
      data-test-chip-value={stringValue}
      disabled={isDisabled}
      onClick={handleSelect}
      role="option"
      type="button"
      {...props}
    >
      <div className={styles.wrapper}>
        <div className={styles.content} id={optionId}>
          {children}
        </div>
        {showClearButton ? (
          <div className={styles.clear}>
            <div className={styles.clearIcon}>
              <IconCrossRoundedFilled aria-hidden="true" />
            </div>
          </div>
        ) : null}
      </div>
    </button>
  );
}
